import { useState, useEffect, useRef } from "react";
import {
  Grid,
  TextField,
  Typography,
  FormControl,
  RadioGroup,
  Radio,
  FormControlLabel,
  Select,
  Menu,
  MenuItem,
  ListItemIcon,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Checkbox,
  FormGroup,
  IconButton,
  Box,
  Divider,
  CircularProgress,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import dayjs from "dayjs";
import request from "../../../utilities/requests";
import { toast } from "../../../utilities/SnackBarUtils";

const Ticketing = ({
  isMobile,
  capacity,
  setCapacity,
  canRegisterInApp,
  setCanRegisterInApp,
  registrationLink,
  setRegistrationLink,
  event,
  eventType,
  setEventType,
  questionGroup,
  setQuestionGroup,
  questionGroups,
  start,
  eventChannels,
}) => {
  const [sellTickets, setSellTickets] = useState(null);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [tnfmSales, setTnfmSales] = useState(null);
  const [ticketTemplates, setTicketTemplates] = useState([]);
  const [externalTT, setExternalTT] = useState([]);
  const [externalSales, setExternalSales] = useState(null);

  const [addOpen, setAddOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [priceDollars, setPriceDollars] = useState("");
  const [maxPerOrder, setMaxPerOrder] = useState(undefined);
  const [quantityTotal, setQuantityTotal] = useState(1);
  const [salesStartAt, setSalesStartAt] = useState(
    dayjs().format("YYYY-MM-DDTHH:mm")
  );
  const [salesEndAt, setSalesEndAt] = useState(
    start ? dayjs(start).format("YYYY-MM-DDTHH:mm") : ""
  );
  const [isVisible, setIsVisible] = useState(true);
  const [isFree, setIsFree] = useState(false);
  const [externalTicketTypeId, setExternalTicketTypeId] = useState(null);
  const [externalEventId, setExternalEventId] = useState(null);
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null); // holds the ticket doc when editing
  const [disableEdit, setDisableEdit] = useState(false);
  const [syncQtyExternal, setSyncQtyExternal] = useState(false);
  const userChangedSellTickets = useRef(false);

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuTicket, setMenuTicket] = useState(null);
  const menuOpen = Boolean(menuAnchorEl);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [autoMapLoading, setAutoMapLoading] = useState(false);

  useEffect(() => {
    if (event) getTicketTypes();
    if (event?.source !== "tnfm") {
      setDisableEdit(true);
    }
  }, [event]);

  useEffect(() => {
    getTicketTemplates();
  }, []);

  useEffect(() => {
    if (!Array.isArray(eventChannels)) return;
    const tnfm = eventChannels.find((c) => c.provider === "tnfm");
    const enabled = !!(tnfm && tnfm.enabled);
    setSellTickets(enabled);
    const eb = eventChannels.find((c) => c.provider === "eventbrite");
    if (eb && externalTT.length === 0) getExternalTicketTypes("eventbrite");
  }, [eventChannels]);

  useEffect(() => {
    if (!userChangedSellTickets.current) return; // ignore programmatic/init changes
    (async () => {
      await updateChannel();
      // reset the flag after syncing
      userChangedSellTickets.current = false;
    })();
  }, [sellTickets]);

  const getTicketTypes = async () => {
    const result = await request(
      `ticketing/tickettype/event?eventId=${event._id}`
    );
    if (result.success) {
      setTicketTypes(result.data?.ticketTypes);
      setTnfmSales(result?.data?.totalSales);
    }
  };

  const getExternalTicketTypes = async (provider) => {
    const result = await request(
      `ticketing/tickettype/external/event?eventId=${event._id}&provider=${provider}`
    );
    if (result.success) {
      setExternalTT(result.data?.ticketTypes);
      setExternalSales(result.data.totalSales);
    }
  };

  const getTicketTemplates = async () => {
    const result = await request(`ticketing/tickettype/template`);
    if (result.success) {
      setTicketTemplates(result.data);
    }
  };

  const getTicketTemplate = async (templateId) => {
    const result = await request(
      `ticketing/tickettype/template?templateId=${templateId}`
    );
    if (result.success) {
      return result.data;
    }
  };

  const mapTicketTypes = async () => {
    setAutoMapLoading(true);
    try {
      const body = { eventId: event._id, provider: "eventbrite" };
      const result = await request(
        "ticketing/tickettype/event/map",
        "post",
        body
      );
      if (result.success) {
        toast.success("Ticket types mapped and synced");
        setTicketTypes(result.data);
      }
    } catch (e) {
      toast.error(e.message);
    }
    setAutoMapLoading(false);
  };

  const resetForm = () => {
    setEditingTicket(null);
    setSelectedTemplateId("");
    setName("");
    setDescription("");
    setPriceDollars("");
    setMaxPerOrder(3);
    setQuantityTotal(1);
    setSalesStartAt(dayjs().format("YYYY-MM-DDTHH:mm"));
    setSalesEndAt(start ? dayjs(start).format("YYYY-MM-DDTHH:mm") : "");
    setIsVisible(true);
    setSaveAsTemplate(false);
    setIsFree(false);
    setExternalTicketTypeId(null);
    setExternalEventId(null);
    setSyncQtyExternal(false);
  };

  const handleTemplateSelect = async (templateId) => {
    if (!templateId) return; // none selected
    const tpl = await getTicketTemplate(templateId);
    if (tpl) {
      setName(tpl.name || "");
      setDescription(tpl.description || "");
      setPriceDollars(((tpl.defaultPriceCents ?? 0) / 100).toFixed(2));
      setMaxPerOrder(tpl.maxPerOrder ?? undefined);
      setIsFree(Boolean(tpl.isFree));
    }
  };

  const updateChannel = async () => {
    const body = { eventId: event._id, data: { enabled: sellTickets } };
    const result = await request("events/eventchannel", "post", body);
    if (result.success && sellTickets) {
      toast.success("Event channel added successfully");
    } else if (result.success && !sellTickets) {
      toast.success("Event channel removed successfully");
    } else {
      toast.error(result.error);
    }
  };

  const openMenu = (e, tt) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
    setMenuTicket(tt);
  };
  const closeMenu = (keepTicket = false) => {
    setMenuAnchorEl(null);
    if (!keepTicket) setMenuTicket(null);
  };

  const handleDeleteTicket = async () => {
    try {
      const tt = menuTicket;
      if (!tt) return;
      const res = await request("ticketing/tickettype/event/remove", "post", {
        ticketTypeId: tt._id,
      });
      if (res?.success) {
        toast.success("Ticket deleted");
        await getTicketTypes();
      }
    } catch (e) {
      toast.error(e.message || "Failed to delete ticket");
    } finally {
      setConfirmOpen(false);
      setMenuTicket(null);
    }
  };

  const handleAddTicket = async () => {
    setLoading(true);
    try {
      const isEdit = !!editingTicket;

      // Price handling: force 0 for free tickets; otherwise parse & clamp >= 0
      const parsedPrice = Number.isFinite(parseFloat(priceDollars))
        ? Math.max(0, parseFloat(priceDollars))
        : 0;
      const priceCents = isFree ? 0 : Math.round(parsedPrice * 100);

      if (isEdit) {
        const updates = {
          name,
          description,
          priceCents,
          maxPerOrder: maxPerOrder ?? undefined,
          quantityTotal: Number(quantityTotal) || 0,
          salesStartAt: salesStartAt ? dayjs(salesStartAt).toISOString() : null,
          salesEndAt: salesEndAt ? dayjs(salesEndAt).toISOString() : null,
          isVisible,
          isFree, // include isFree when updating
          syncQtyExternal
        };

        const res = await request("ticketing/tickettype/event/update", "post", {
          ticketTypeId: editingTicket._id,
          updates,
        });

        if (res?.success) {
          await getTicketTypes();
          await getExternalTicketTypes('eventbrite');
          setAddOpen(false);
          setEditingTicket(null);
        } else {
          toast.error(res?.error || "Failed to update ticket");
        }
      } else {
        const body = {
          eventId: event._id,
          name,
          description,
          priceCents,
          maxPerOrder: maxPerOrder ?? undefined,
          quantityTotal: Number(quantityTotal) || 0,
          salesStartAt: salesStartAt ? dayjs(salesStartAt).toISOString() : null,
          salesEndAt: salesEndAt ? dayjs(salesEndAt).toISOString() : null,
          status: "on_sale",
          isVisible,
          isFree, // include isFree when creating
          saveAsTemplate,
        };

        const res = await request(
          "ticketing/tickettype/event/create",
          "post",
          body
        );

        if (res?.success) {
          await getTicketTypes();
          setAddOpen(false);
        } else {
          toast.error(res?.error || "Failed to create ticket");
        }
      }
    } catch (e) {
      toast.error(e.message || "Failed to save ticket");
    } finally {
      setLoading(false);
    }
  };

  // Helper to format ticket status
  const formatStatus = (tt) => {
    const sold = Number(tt.quantitySold ?? 0);
    const total = Number(tt.quantityTotal ?? 0);
    const now = dayjs();

    if (total > 0 && sold >= total) {
      return { label: "Sold Out", color: "error.main" };
    }

    if (!tt.isVisible) {
      return { label: "Hidden", color: "error.main" };
    }
    const starts = tt.salesStartAt ? dayjs(tt.salesStartAt) : null;
    const ends = tt.salesEndAt ? dayjs(tt.salesEndAt) : null;

    if (starts && now.isBefore(starts)) {
      return {
        label: `Scheduled • Starts ${starts.format("M/D h:mm A")}`,
        color: "text.disabled",
      };
    }
    if (ends && now.isAfter(ends)) {
      return { label: "Ended", color: "text.disabled" };
    }
    let suffix = "";
    if (ends && now.isBefore(ends))
      suffix = ` • Ends ${ends.format("M/D [at] h:mm A")}`;
    return { label: `On Sale${suffix}`, color: "info.main" };
  };

  // Helper to total quantities for external ticket types
  const externalTotals = (items = []) => {
    return items.reduce(
      (acc, t) => {
        acc.total += Number(t.quantityTotal || 0);
        acc.sold += Number(t.quantitySold || 0);
        return acc;
      },
      { total: 0, sold: 0 }
    );
  };

  const formatExternalStatus = (t) => {
    const now = dayjs();
    const starts = t.salesStartAt ? dayjs(t.salesStartAt) : null;
    const ends = t.salesEndAt ? dayjs(t.salesEndAt) : null;
    // Prefer explicit status when provided
    if (t.status === "SOLD_OUT")
      return { label: "Sold Out", color: "error.main" };
    if (t.status === "AVAILABLE") {
      let suffix = "";
      if (ends && now.isBefore(ends))
        suffix = ` • Ends ${ends.format("M/D [at] h:mm A")}`;
      if (starts && now.isBefore(starts))
        return {
          label: `Scheduled • Starts ${starts.format("M/D h:mm A")}`,
          color: "text.disabled",
        };
      return { label: `On Sale${suffix}`, color: "info.main" };
    }
    if (ends && now.isAfter(ends))
      return { label: "Ended", color: "text.disabled" };
    if (starts && now.isBefore(starts))
      return {
        label: `Scheduled • Starts ${starts.format("M/D h:mm A")}`,
        color: "text.disabled",
      };
    return { label: t.status || "Unknown", color: "text.disabled" };
  };

  // Helper to render external sales amount
  const ExternalSalesFooter = () => {
    if (externalSales === null || externalSales === undefined) return null;
    const formatted =
      typeof externalSales === "number"
        ? externalSales.toLocaleString(undefined, {
            style: "currency",
            currency: "USD",
          })
        : String(externalSales);
    return (
      <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2, py: 1 }}>
        <Typography variant="h6" color="text.secondary">
          Total Sales: {formatted}
        </Typography>
      </Box>
    );
  };

  return (
    <>
      <Grid item>
        <TextField
          style={{ width: isMobile ? "100%" : "25%" }}
          label="Capacity *"
          type="number"
          inputProps={{ step: 1 }}
          value={capacity}
          onChange={(e) => setCapacity(e.target.value)}
          disabled={disableEdit}
        />
      </Grid>
      <Grid item style={{ height: 100 }}>
        <FormControl
          component="fieldset"
          style={{ width: isMobile ? "100%" : "25%" }}
        >
          <Typography variant="body2" gutterBottom>
            Are you selling tickets on this platform?
          </Typography>
          <RadioGroup
            row
            value={sellTickets ? "yes" : "no"}
            onChange={(e) => {
              userChangedSellTickets.current = true;
              setSellTickets(e.target.value === "yes");
            }}
          >
            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="no" control={<Radio />} label="No" />
          </RadioGroup>
        </FormControl>
      </Grid>
      {sellTickets ? null : (
        <Grid item style={{}}>
          <TextField
            style={{ width: isMobile ? "100%" : "50%" }}
            label={
              canRegisterInApp === false
                ? "Registration Link *"
                : "Registration Link (optional)"
            }
            value={registrationLink}
            onChange={(e) => setRegistrationLink(e.target.value)}
          />
          <Typography
            variant="caption"
            style={{ display: "block", marginTop: 4 }}
          >
            Required if you are not selling tickets in the app
          </Typography>
        </Grid>
      )}
      {sellTickets ? (
        <Grid item>
          <Button
            variant="contained"
            onClick={mapTicketTypes}
            style={{ width: 110 }}
          >
            {autoMapLoading ? (
              <CircularProgress size={24} style={{ color: "white" }} />
            ) : (
              "Auto Map"
            )}
          </Button>
        </Grid>
      ) : null}
      {sellTickets ? (
        <>
          {/* Two-column responsive layout */}
          <Grid
            item
            style={{ width: "100%", paddingLeft: 0, paddingRight: 24 }}
          >
            <Grid container spacing={2}>
              {/* Left column: TNFM tickets */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    bgcolor: "background.paper",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      px: 2,
                      py: 1.5,
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      TNFM Tickets
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Sold:{" "}
                      {ticketTypes.reduce(
                        (s, t) => s + Number(t.quantitySold || 0),
                        0
                      )}
                      /
                      {ticketTypes.reduce(
                        (s, t) => s + Number(t.quantityTotal || 0),
                        0
                      )}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box>
                    {ticketTypes && ticketTypes.length > 0 ? (
                      <>
                        <List sx={{ width: "100%" }}>
                          {ticketTypes.map((tt) => (
                            <ListItem
                              key={tt._id}
                              divider
                              disableGutters
                              sx={{ py: 0 }}
                            >
                              <ListItemButton
                                onClick={() => {
                                  setEditingTicket(tt);
                                  setSelectedTemplateId("");
                                  setName(tt.name || "");
                                  setDescription(tt.description || "");
                                  setPriceDollars(
                                    ((tt.priceCents ?? 0) / 100).toFixed(2)
                                  );
                                  setMaxPerOrder(tt.maxPerOrder ?? undefined);
                                  setQuantityTotal(tt.quantityTotal ?? 0);
                                  setSalesStartAt(
                                    tt.salesStartAt
                                      ? dayjs(tt.salesStartAt).format(
                                          "YYYY-MM-DDTHH:mm"
                                        )
                                      : dayjs().format("YYYY-MM-DDTHH:mm")
                                  );
                                  setSalesEndAt(
                                    tt.salesEndAt
                                      ? dayjs(tt.salesEndAt).format(
                                          "YYYY-MM-DDTHH:mm"
                                        )
                                      : start
                                      ? dayjs(start).format("YYYY-MM-DDTHH:mm")
                                      : ""
                                  );
                                  setIsVisible(tt.isVisible !== false);
                                  setIsFree(tt.isFree === true);
                                  setExternalTicketTypeId(
                                    tt.externalTicketTypeId
                                  );
                                  setExternalEventId(tt.externalEventId);
                                  setSaveAsTemplate(false);
                                  setAddOpen(true);
                                  setSyncQtyExternal(
                                    Boolean(tt.externalTicketTypeId)
                                  );
                                }}
                                sx={{ px: 1.5 }}
                              >
                                <Grid container alignItems="center" spacing={1}>
                                  <Grid
                                    item
                                    sx={{
                                      display: { xs: "none", sm: "block" },
                                    }}
                                  >
                                    <DragIndicatorIcon
                                      fontSize="small"
                                      sx={{ color: "text.disabled" }}
                                    />
                                  </Grid>
                                  <Grid item xs>
                                    <Typography
                                      variant="subtitle1"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      {tt.name}
                                    </Typography>
                                    {(() => {
                                      const st = formatStatus(tt);
                                      return (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            // mt: 0.5,
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              width: 8,
                                              height: 8,
                                              borderRadius: "50%",
                                              bgcolor: st.color,
                                            }}
                                          />
                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                          >
                                            {st.label}
                                          </Typography>
                                        </Box>
                                      );
                                    })()}
                                  </Grid>
                                  <Grid item>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ mr: 2 }}
                                    >
                                      Sold:&nbsp;{Number(tt.quantitySold ?? 0)}/
                                      {Number(tt.quantityTotal ?? 0)}
                                    </Typography>
                                  </Grid>
                                  <Grid item>
                                    <Typography
                                      variant="body1"
                                      sx={{ minWidth: 72, textAlign: "right" }}
                                    >
                                      {tt.isFree
                                        ? "Free"
                                        : `$${(
                                            (tt.priceCents ?? 0) / 100
                                          ).toFixed(2)}`}
                                    </Typography>
                                  </Grid>
                                  <Grid item>
                                    <IconButton
                                      edge="end"
                                      size="small"
                                      onClick={(e) => openMenu(e, tt)}
                                    >
                                      <MoreVertIcon />
                                    </IconButton>
                                  </Grid>
                                </Grid>
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </List>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            px: 2,
                            py: 1,
                          }}
                        >
                          <Typography variant="h6" color="text.secondary">
                            Total Sales: {tnfmSales}
                          </Typography>
                        </Box>
                      </>
                    ) : (
                      <Typography variant="body2" sx={{ px: 1.5, py: 1 }}>
                        There are no tickets for this event yet.
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box sx={{ pt: 1.5 }}>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setEditingTicket(null);
                      setAddOpen(true);
                      resetForm();
                    }}
                  >
                    Add Ticket
                  </Button>
                </Box>
              </Grid>

              {/* Right column: External tickets (only if present) */}
              {externalTT && externalTT.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      bgcolor: "background.paper",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        px: 2,
                        py: 1.5,
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Eventbrite Tickets
                      </Typography>
                      {(() => {
                        const totals = externalTotals(externalTT);
                        return (
                          <Typography variant="body2" color="text.secondary">
                            Sold: {totals.sold}/{totals.total}
                          </Typography>
                        );
                      })()}
                    </Box>
                    <Divider />
                    <List sx={{ width: "100%" }}>
                      {externalTT.map((t, idx) => (
                        <ListItem
                          key={`${t.name}-${idx}`}
                          divider
                          disableGutters
                          sx={{ py: 0 }}
                        >
                          <ListItemButton>
                            <Grid container alignItems="center" spacing={1}>
                              <Grid item xs>
                                <Typography
                                  variant="subtitle1"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {t.name}
                                </Typography>
                                {(() => {
                                  const st = formatExternalStatus(t);
                                  return (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        // mt: 0.5,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: 8,
                                          height: 8,
                                          borderRadius: "50%",
                                          bgcolor: st.color,
                                        }}
                                      />
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        {st.label}
                                      </Typography>
                                    </Box>
                                  );
                                })()}
                              </Grid>
                              <Grid item>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mr: 2 }}
                                >
                                  Sold:&nbsp;{Number(t.quantitySold || 0)}/
                                  {Number(t.quantityTotal || 0)}
                                </Typography>
                              </Grid>
                              <Grid item>
                                <Typography
                                  variant="body1"
                                  sx={{ minWidth: 72, textAlign: "right" }}
                                >
                                  {t.isFree ? "Free" : t.price}
                                </Typography>
                              </Grid>
                            </Grid>
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                    <ExternalSalesFooter />
                  </Box>
                </Grid>
              )}
            </Grid>
          </Grid>

          {/* Dialogs and menus remain unchanged */}
          {/** everything below stays the same **/}
          <Dialog
            open={addOpen}
            onClose={() => setAddOpen(false)}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>
              {editingTicket ? "Edit Ticket" : "Add Ticket"}
            </DialogTitle>
            {editingTicket && (
              <Typography
                variant="caption"
                sx={{ px: 3, pt: 1, display: "block" }}
              >
                Editing existing ticket. Template selection is disabled.
              </Typography>
            )}
            <DialogContent dividers>
              <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="tpl-label">Use Template</InputLabel>
                    <Select
                      labelId="tpl-label"
                      label="Use Template"
                      value={selectedTemplateId}
                      disabled={!!editingTicket}
                      onChange={async (e) => {
                        const id = e.target.value;
                        setSelectedTemplateId(id);
                        await handleTemplateSelect(id);
                      }}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {ticketTemplates.map((tpl) => (
                        <MenuItem key={tpl._id} value={tpl._id}>
                          {tpl.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography
                    variant="caption"
                    sx={{ mt: 0.5, display: "block" }}
                  >
                    Selecting a template will prefill the fields below.
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Ticket Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    minRows={2}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Quantity"
                    value={quantityTotal}
                    onChange={(e) => setQuantityTotal(Number(e.target.value))}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Price"
                    value={priceDollars}
                    onChange={(e) => setPriceDollars(e.target.value)}
                    disabled={isFree}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Per Order"
                    value={maxPerOrder ?? ""}
                    onChange={(e) => setMaxPerOrder(Number(e.target.value))}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Sales Start"
                    type="datetime-local"
                    InputLabelProps={{ shrink: true }}
                    value={salesStartAt}
                    onChange={(e) => setSalesStartAt(e.target.value)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Sales End"
                    type="datetime-local"
                    InputLabelProps={{ shrink: true }}
                    value={salesEndAt}
                    onChange={(e) => setSalesEndAt(e.target.value)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isFree}
                        onChange={(e) => {
                          const next = e.target.checked;
                          setIsFree(next);
                          if (next) setPriceDollars("0.00"); // zero out price when free
                        }}
                      />
                    }
                    label="This is a free ticket"
                  />
                </Grid>
                {editingTicket && externalTicketTypeId ? (
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={syncQtyExternal}
                          onChange={(e) => setSyncQtyExternal(e.target.checked)}
                        />
                      }
                      label="Sync quantity with other ticket platforms"
                    />
                  </Grid>
                ) : null}

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isVisible}
                        onChange={(e) => setIsVisible(e.target.checked)}
                      />
                    }
                    label="Visible to buyers"
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={saveAsTemplate}
                        onChange={(e) => setSaveAsTemplate(e.target.checked)}
                      />
                    }
                    label="Save this ticket type as a template"
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAddOpen(false)} disabled={loading}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleAddTicket}
                disabled={
                  loading ||
                  !name ||
                  !Number.isFinite(Number(quantityTotal)) ||
                  Number(quantityTotal) <= 0 ||
                  (!isFree &&
                    (!Number.isFinite(parseFloat(priceDollars)) ||
                      parseFloat(priceDollars) < 0))
                }
              >
                {loading ? "Saving…" : "Save Ticket"}
              </Button>
            </DialogActions>
          </Dialog>
          <Menu
            anchorEl={menuAnchorEl}
            open={menuOpen}
            onClose={closeMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem
              onClick={() => {
                closeMenu(true); // keep the selected ticket in state
                setConfirmOpen(true);
              }}
              sx={{ color: "error.main" }}
            >
              <ListItemIcon>
                <DeleteOutlineIcon fontSize="small" />
              </ListItemIcon>
              Delete
            </MenuItem>
          </Menu>
          <Dialog
            open={confirmOpen}
            onClose={() => {
              setConfirmOpen(false);
              setMenuTicket(null);
            }}
          >
            <DialogTitle>Delete ticket?</DialogTitle>
            <DialogContent>
              <DialogContentText>
                {menuTicket
                  ? `Delete ticket “${menuTicket.name}”? This cannot be undone.`
                  : "Are you sure you want to delete this ticket? This cannot be undone."}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
              <Button
                color="error"
                variant="contained"
                onClick={handleDeleteTicket}
                autoFocus
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ) : null}

      {/* Show external tickets even when TNFM selling is OFF */}
      {!sellTickets && externalTT && externalTT.length > 0 && (
        <Grid item sx={{ width: "100%" }}>
          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              bgcolor: "background.paper",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2,
                py: 1.5,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Eventbrite Tickets
              </Typography>
              {(() => {
                const totals = externalTotals(externalTT);
                return (
                  <Typography variant="body2" color="text.secondary">
                    Sold: {totals.sold}/{totals.total}
                  </Typography>
                );
              })()}
            </Box>
            <Divider />
            <List sx={{ width: "100%" }}>
              {externalTT.map((t, idx) => (
                <ListItem
                  key={`${t.name}-${idx}`}
                  divider
                  disableGutters
                  sx={{ py: 1, px: 1.5 }}
                >
                  <Grid container alignItems="center" spacing={1}>
                    <Grid item xs>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {t.name}
                      </Typography>
                      {(() => {
                        const st = formatExternalStatus(t);
                        return (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mt: 0.5,
                            }}
                          >
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                bgcolor: st.color,
                              }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              {st.label}
                            </Typography>
                          </Box>
                        );
                      })()}
                    </Grid>
                    <Grid item>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mr: 2 }}
                      >
                        Sold:&nbsp;{Number(t.quantitySold || 0)}/
                        {Number(t.quantityTotal || 0)}
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Typography
                        variant="body1"
                        sx={{ minWidth: 72, textAlign: "right" }}
                      >
                        {t.price}
                      </Typography>
                    </Grid>
                  </Grid>
                </ListItem>
              ))}
            </List>
            <ExternalSalesFooter />
          </Box>
        </Grid>
      )}
    </>
  );
};

export default Ticketing;
